import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  AlertTriangle,
  BellRing,
  Clock,
  Wrench,
  ShieldAlert,
  Users,
  GraduationCap,
  ArrowRight,
  Activity,
  ClipboardCheck,
} from "lucide-react";
import { useOperationsFeed } from "../lib/operationsFeed";
import { useT } from "../lib/i18n";

/**
 * useSmoothCount — restrained, frame-paced count interpolation.
 *
 * The pressure panel must NEVER flash; values drift to their new state
 * over ~400ms with an ease-out curve so a prospect sees the system
 * "absorb" the change. First render uses the target value as-is (no
 * pop on mount). Hard-reset on prop kind change.
 */
function useSmoothCount(target, durationMs = 400) {
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (typeof target !== "number" || target === display) return;
    fromRef.current = display;
    startRef.current = null;
    const tick = (t) => {
      if (startRef.current == null) startRef.current = t;
      const elapsed = t - startRef.current;
      const k = Math.min(1, elapsed / durationMs);
      // easeOutCubic — soft landing, no overshoot.
      const eased = 1 - Math.pow(1 - k, 3);
      const next = Math.round(fromRef.current + (target - fromRef.current) * eased);
      setDisplay(next);
      if (k < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // We intentionally exclude `display` so we don't re-trigger the
    // animation each frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return display;
}

/**
 * OperationalPressurePanel
 * --------------------------------------------------
 * Shows the operational TENSION the system is currently absorbing.
 * Restrained 6-up grid of real operational categories with current
 * counts. No flashing, no animations beyond a subtle hover lift.
 *
 * Each tile communicates: "the system is actively tracking this and
 * routing it to the right people right now." Counts are demo-seeded
 * but realistic for a 3-job heavy-civil contractor.
 *
 * Visual restraint: numerals dominate, copy stays mono, severity is
 * indicated by left-edge bar color only — not a glowing alert chrome.
 */

const ITEMS = [
  {
    key: "open_incidents",
    icon: ShieldAlert,
    severity: "critical",
    label: "Open incidents",
    value: 2,
    detail: "1 stop-the-line · 1 first-aid",
    foot: "Safety lead paged · CAs queued",
  },
  {
    key: "equipment_lockouts",
    icon: Wrench,
    severity: "warn",
    label: "Equipment locked out",
    value: 4,
    detail: "Pre-Op fail · awaiting RTS",
    foot: "Avg downtime 6h 12m",
  },
  {
    key: "overdue_approvals",
    icon: Clock,
    severity: "warn",
    label: "Overdue approvals",
    value: 7,
    detail: "PM backlog · oldest 22h",
    foot: "Escalation T-2h",
  },
  {
    key: "manpower_short",
    icon: Users,
    severity: "warn",
    label: "Manpower short",
    value: 2,
    detail: "Job 2110 · Crew A-1",
    foot: "Backfill request open",
  },
  {
    key: "certs_expired",
    icon: GraduationCap,
    severity: "info",
    label: "Certifications expired",
    value: 3,
    detail: "OSHA-30 · 2 ops · 1 sup",
    foot: "Training auto-assigned",
  },
  {
    key: "pending_cas",
    icon: AlertTriangle,
    severity: "info",
    label: "Pending corrective actions",
    value: 11,
    detail: "All within SLA",
    foot: "9 owners notified",
  },
  {
    key: "open_daily_reports",
    icon: ClipboardCheck,
    severity: "info",
    label: "Daily reports today",
    value: 5,
    detail: "All routed · PDFs generated",
    foot: "PM visibility · same-day",
  },
  {
    key: "pending_safety_acks",
    icon: BellRing,
    severity: "info",
    label: "Pending safety acks",
    value: 4,
    detail: "Toolbox talks · accruing signatures",
    foot: "Auto-routed to crew profiles",
  },
];

const SEV = {
  critical: "#FF6A6A",
  warn:     "#FFB000",
  info:     "#FF6A00",
};

export default function OperationalPressurePanel() {
  const { t } = useT();
  // Live operations feed — gives real DB counts as the tile baseline.
  // When the backend falls back to seed (DB empty), we keep the
  // hardcoded baselines so the panel never goes blank.
  const { pressure: livePressure } = useOperationsFeed(1);

  // Effective items — overlay live counts on top of the static defaults
  // without disturbing copy, severity or order.
  const items = useMemo(() => {
    if (!livePressure) return ITEMS;
    return ITEMS.map((it) => {
      const live = livePressure[it.key];
      if (typeof live === "number" && live >= 0) {
        return { ...it, value: live };
      }
      return it;
    });
  }, [livePressure]);

  // Live deltas — the panel reacts subtly to real demo submissions so
  // a prospect SEES the system absorb their action. Each delta is a
  // small numeric bump on a specific tile that decays back to the
  // baseline 6s later (so the next visitor sees a clean state).
  const [deltas, setDeltas] = useState({});                  // {tileIdx: +n}
  const [pulseIdx, setPulseIdx] = useState(null);             // tile that should briefly highlight
  const [recentEvent, setRecentEvent] = useState(null);       // banner copy
  const decayTimer = useRef(null);

  useEffect(() => {
    const onSubmit = (ev) => {
      const kind = (ev?.detail?.kind || "").toLowerCase();
      // Map kind → which tile bumps + transient banner copy
      let tileIdx = null;
      let banner = null;
      if (kind === "toolbox-talk") {
        tileIdx = 5;   // Pending CAs row — rises by 1 (a CA could be opened from review)
        banner  = `Toolbox Talk routed · ${ev?.detail?.attendees ?? 7} acks logged · compliance refreshed`;
      } else if (kind === "incident" || kind === "near-miss") {
        tileIdx = 0;   // Open incidents
        banner  = "Incident submitted · safety lead paged · CA queued";
      } else if (kind === "preop-fail") {
        tileIdx = 1;   // Equipment locked out
        banner  = "Pre-Op FAIL · unit locked out · shop notified";
      } else if (kind === "daily-report") {
        tileIdx = 2;   // Overdue approvals adjusts (PM has fresh work)
        banner  = "Daily Report routed to PM · PDF generated · KPI rolled";
      }
      if (tileIdx === null) return;

      setDeltas((d) => ({ ...d, [tileIdx]: (d[tileIdx] || 0) + 1 }));
      setPulseIdx(tileIdx);
      setRecentEvent({ banner, at: Date.now() });

      // Clear pulse after 1.4s
      setTimeout(() => setPulseIdx((cur) => (cur === tileIdx ? null : cur)), 1400);

      // Decay the delta back to 0 after 6s (clean state for next visitor)
      if (decayTimer.current) clearTimeout(decayTimer.current);
      decayTimer.current = setTimeout(() => {
        setDeltas({});
        setRecentEvent(null);
      }, 6000);
    };
    window.addEventListener("forgeops:submission", onSubmit);
    return () => {
      window.removeEventListener("forgeops:submission", onSubmit);
      if (decayTimer.current) clearTimeout(decayTimer.current);
    };
  }, []);

  return (
    <section
      className="rounded-md overflow-hidden"
      style={{ background: "#0A0A0A", border: "1px solid #1A1A1A" }}
      data-testid="operational-pressure-panel"
    >
      <div
        className="flex items-center justify-between px-5 py-4 border-b flex-wrap gap-2"
        style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}
      >
        <div className="flex items-center gap-2">
          <span
            className="font-mono text-[10px] uppercase tracking-[0.3em] font-bold"
            style={{ color: "#FF6A00" }}
          >
            {t("Operational Pressure · Active Load")}
          </span>
        </div>
        <span
          className="font-mono text-[10px] uppercase tracking-[0.25em]"
          style={{ color: "#666" }}
        >
          {t("Updated continuously · 3 jobs · 47 ops")}
        </span>
      </div>

      {/* Transient submission-reaction banner */}
      {recentEvent ? (
        <div
          className="flex items-center gap-2.5 px-5 py-2 border-b"
          style={{
            background: "rgba(255,106,0,0.06)",
            borderColor: "rgba(255,106,0,0.35)",
            animation: "fo-pressure-banner-in 320ms ease-out",
          }}
          data-testid="pressure-live-banner"
        >
          <Activity className="w-3.5 h-3.5" style={{ color: "#FF6A00" }} />
          <span
            className="font-mono text-[9px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 rounded"
            style={{ background: "#FF6A00", color: "#000" }}
          >
            {t("System reacted")}
          </span>
          <span className="font-mono text-[11px]" style={{ color: "#E0E0E0" }}>
            {recentEvent.banner}
          </span>
        </div>
      ) : null}

      <div
        className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-px"
        style={{ background: "#1A1A1A" }}
      >
        {items.map((it, i) => {
          const Icon = it.icon;
          const accent = SEV[it.severity];
          const delta = deltas[i] || 0;
          const isPulsing = pulseIdx === i;
          const target =
            typeof it.value === "number" ? it.value + delta : it.value;
          return <PressureTile
            key={it.key}
            it={it}
            i={i}
            Icon={Icon}
            accent={accent}
            delta={delta}
            isPulsing={isPulsing}
            target={target}
            t={t}
          />;
        })}
      </div>

      <style>{`
        @keyframes fo-pressure-banner-in {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}


function PressureTile({ it, i, Icon, accent, delta, isPulsing, target, t }) {
  const displayValue = useSmoothCount(target);
  return (
    <div
      className="p-4 sm:p-5 transition-all relative"
      style={{
        background: isPulsing ? "#161616" : "#0F0F0F",
        borderLeft: `2px solid ${accent}`,
        boxShadow: isPulsing ? `inset 0 0 0 1px ${accent}55` : "none",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#141414")}
      onMouseLeave={(e) =>
        (e.currentTarget.style.background = isPulsing ? "#161616" : "#0F0F0F")
      }
      data-testid={`pressure-tile-${i}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span
          className="inline-flex items-center justify-center w-8 h-8 rounded"
          style={{
            background: `${accent}1F`,
            color: accent,
            border: `1px solid ${accent}55`,
          }}
        >
          <Icon className="w-4 h-4" />
        </span>
        <ArrowRight className="w-3.5 h-3.5" style={{ color: "#444" }} />
      </div>
      <div className="font-display text-3xl font-black tracking-tight text-white leading-none">
        {displayValue}
        {delta > 0 && (
          <span
            className="font-mono text-[10px] tracking-wide font-bold ml-1.5"
            style={{ color: "#FF6A00" }}
          >
            +{delta}
          </span>
        )}
      </div>
      <div
        className="font-mono text-[9px] uppercase tracking-[0.25em] mt-2 font-bold"
        style={{ color: "#FFFFFF" }}
      >
        {t(it.label)}
      </div>
      <div
        className="font-mono text-[10px] mt-1 leading-snug"
        style={{ color: "#BDBDBD" }}
      >
        {it.detail}
      </div>
      <div
        className="font-mono text-[9px] mt-2 leading-snug"
        style={{ color: "#666" }}
      >
        {it.foot}
      </div>
    </div>
  );
}
